import type { AraConfigurePayload } from '@/features/ara-generation/types/ara-configure'
import type { AraEditTarget } from '@/features/ara-generation/types/ara-events'
import type { ChatMessage } from '@/features/ara-generation/types/ara-chat'
import type { PartialGenerationBrief } from '@/features/ara-generation/types/generation-brief'

/** A preview re-render that only echoes content the iframe already shows. */
export type AraEchoRender = { base: string; html: string }

export type AraGenerationPhase = 'configure' | 'streaming' | 'done' | 'error'
export type AraGenerationStage = 'preparing' | 'generating' | 'finalizing'

export type AraSectionProgress = {
  status: string
  elapsed_ms: number
  chars: number
}

export type AraRequestedSection = {
  key: string
  label: string
}

export type AraDraftPayload = {
  assessmentId: string
  variantId: string
  sections: Record<string, string>
  structuredAra: Record<string, unknown>
  lastRenderedHtml: string
  sectionOrder: string[]
  customSections: AraConfigurePayload['customSections']
}

export type AraIntakeMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
}

export type AraDraftIntake = {
  messages: AraIntakeMessage[]
  brief: PartialGenerationBrief
  updatedAt: number
}

export type AraVariantEditState = {
  messages: ChatMessage[]
  sections: Record<string, string>
  structuredAra: Record<string, unknown>
  lastRenderedHtml: string
  streaming: boolean
  focusedSectionKey: string | null
  selectedElementTarget: AraEditTarget | null
  previewHtml: string | null
  echoRender?: AraEchoRender | null
  /** Present only while a pending/accepted section proposal INSERTS a new custom
   * chat section: the draft order plus that key. Accept copies it into
   * draftPayload.sectionOrder; previews assemble from it (FE-6245). */
  sectionOrder?: string[]
}

export type AraVariant = {
  id: string
  name: string
  createdAt: number
  /** Server-side draft document id (kind=ara, status draft) created at
   * generation completion. Null for legacy local-only variants. */
  serverDocumentId: string | null
  /** Read-only rendering (Published ARA): inline editing and AI edit
   * affordances are inert. Set from the document status at open time. */
  readOnly: boolean
  /** Persisted "local content is ahead of the server source" marker, set by
   * every content-dirtying change and cleared only by a successful source
   * PUT. Unlike the in-memory sync markers, this survives reload/crash — it
   * is what lets a reopen refuse to replace a fresher local copy with a
   * stale server source ("unsaved changes" indicator). */
  unsyncedEdits?: boolean
  /** When this variant's asset scope was saved (SCP-129): the generation run
   * that carried the selection. Tree assets created after it surface as "new,
   * not included". Null for variants that never ran a scoped generation. */
  scopeSavedAt?: number | null
  phase: AraGenerationPhase
  stage: AraGenerationStage
  sectionProgress: Record<string, AraSectionProgress>
  requestedSections: AraRequestedSection[]
  backboneStatus: string
  backboneStageCount: number
  error: string | null
  configure: AraConfigurePayload | null
  brief: PartialGenerationBrief | null
  draftPayload: AraDraftPayload | null
  edit: AraVariantEditState | null
}

export type AraWorkspace = {
  assessmentId: string
  variants: AraVariant[]
  activeVariantId: string | null
  lastConfigure: AraConfigurePayload | null
  showConfigure: boolean
  draftIntake: AraDraftIntake | null
}

export function emptyWorkspace(assessmentId: string): AraWorkspace {
  return {
    assessmentId,
    variants: [],
    activeVariantId: null,
    lastConfigure: null,
    showConfigure: true,
    draftIntake: null
  }
}

export function emptyVariant(input: {
  id: string
  name: string
  createdAt?: number
  configure?: AraConfigurePayload | null
  brief?: PartialGenerationBrief | null
}): AraVariant {
  return {
    id: input.id,
    name: input.name,
    createdAt: input.createdAt ?? Date.now(),
    phase: 'configure',
    stage: 'preparing',
    sectionProgress: {},
    requestedSections: [],
    backboneStatus: '',
    backboneStageCount: 0,
    error: null,
    configure: input.configure ?? null,
    brief: input.brief ?? null,
    draftPayload: null,
    edit: null,
    serverDocumentId: null,
    readOnly: false,
    scopeSavedAt: null
  }
}

export function activeVariant(workspace: AraWorkspace | null | undefined): AraVariant | null {
  if (!workspace?.activeVariantId) return null
  return workspace.variants.find(variant => variant.id === workspace.activeVariantId) ?? null
}
